import "./form-hidden-combi-calendar.scss";
import { useTranslate } from "../../../features/translation/hooks/use-translation";
import {
  Calendar,
  InputHidden,
  Divider,
  Chip,
  IconButton,
  Select,
} from "../../../components";
import { useCheckbox, useInput } from "../../../features/auto-form";
import { useFormContext } from "../../../features/auto-form";
import { useEffect, useLayoutEffect, useState } from "react";
import Toggle from "../../../components/inputs/toggle/toggle";

const MONTHS_OPTIONS = require("../../../pages/group-travel-form/config/data/month-options.json");

const DATE_LOCAL_MAP = {
  se: "sv-SE",
  en: "en-US",
  dk: "da-DK",
  no: "no-NO",
  fi: "fi-FI",
};

const FormHiddenCombiCalendar = (props) => {
  const { market, name, label, placeholder } = props;
  const { t } = useTranslate();
  const { register, deregister, updateValidity } = useFormContext();

  const [type, setType] = useState("month");
  const [textValue, setTextValue] = useState("");
  const [error, setError] = useState(null);
  const [displayError, setDisplayError] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  const YEAR_OPTIONS = [
    { label: new Date().getFullYear(), value: new Date().getFullYear() },
    {
      label: new Date().getFullYear() + 1,
      value: new Date().getFullYear() + 1,
    },
    {
      label: new Date().getFullYear() + 2,
      value: new Date().getFullYear() + 2,
    },
  ];

  const { ref: outboundRef, bind: outboundBind } = useInput({
    ...props,
    name: `${name}.departure.start`,
  });

  const { ref: homeboundRef, bind: homeboundBind } = useInput({
    ...props,
    name: `${name}.departure.end`,
  });

  const { ref: monthRef, bind: monthBind } = useCheckbox({
    ...props,
    name: `${name}.departure`,
  });

  const { ref: typeRef, bind: typeBind } = useInput({
    ...props,
    name: `${name}.type`,
  });

  const translatedLabel = t("input.custom.hiddenCombiCalendar.label") || label;
  const translatedPlaceholder =
    t("input.custom.hiddenCombiCalendar.placeholder") || placeholder;

  const formatMonthsText = (values) => {
    if (!values) return "";

    const grouped = {};
    values.forEach((item) => {
      const [year, month] = item.split(".");
      let monthName = new Date(year, month - 1).toLocaleString(
        DATE_LOCAL_MAP[market],
        { month: "short" }
      );
      monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);

      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(monthName);
    });

    return Object.entries(grouped)
      .map(([year, months]) => `(${year}) ${months.join(", ")}`)
      .join(" ");
  };

  const formatDateText = (outboundValue, homeboundValue) => {
    if (!outboundValue && !homeboundValue) return "";

    const formatDate = (dateValue) => {
      if (!dateValue) return "";
      const date = new Date(dateValue);
      return new Intl.DateTimeFormat(DATE_LOCAL_MAP[market]).format(date);
    };

    const outbound = formatDate(outboundValue);
    const homebound = formatDate(homeboundValue);
    return `${outbound} - ${homebound}`;
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    typeRef.current.setValue(newType);

    if (newType === "month") {
      outboundRef.current.reset();
      homeboundRef.current.reset();

      register(`${name}.departure.range`, monthRef);
      register(`${name}.departure.type`, typeRef);
      deregister(`${name}.departure.start`);
      deregister(`${name}.departure.end`);

      setError(monthBind.error);
      setDisplayError(monthBind.displayError);
    } else {
      monthRef.current.reset();

      register(`${name}.departure.start`, outboundRef);
      register(`${name}.departure.end`, homeboundRef);
      deregister(`${name}.departure`);

      setError(outboundBind.error || homeboundBind.error || null);
      setDisplayError(outboundBind.error || homeboundBind.error);
    }
  };

  useLayoutEffect(() => {
    register(`${name}.departure`, monthRef);
    setError(monthBind.error);
  }, []);

  useEffect(() => {
    handleTypeChange(type);
  }, [type]);

  useEffect(() => {
    if (type === "month") {
      updateValidity(`${name}.departure`, monthBind.error);
      setError(monthBind.error || null);
    } else {
      updateValidity(`${name}.departure.start`, outboundBind.error);
      updateValidity(`${name}.departure.end`, homeboundBind.error);
      setError(outboundBind.error || homeboundBind.error || null);
    }
  }, [outboundBind.error, homeboundBind.error, monthBind.error, type]);

  useEffect(() => {
    if (type === "month") {
      setTextValue(formatMonthsText(monthBind.values));
    } else {
      setTextValue(formatDateText(outboundBind.value, homeboundBind.value));
    }
  }, [outboundBind.value, homeboundBind.value, monthBind.values, type]);

  useEffect(() => {
    if (
      outboundBind.value &&
      homeboundBind.value &&
      new Date(outboundBind.value) > new Date(homeboundBind.value)
    ) {
      homeboundRef.current.reset();
    }
  }, [outboundBind.value]);

  useEffect(() => {
    setDisplayError(
      outboundBind.displayError ||
        homeboundBind.displayError ||
        monthBind.displayError
    );
  }, [
    outboundBind.displayError,
    homeboundBind.displayError,
    monthBind.displayError,
  ]);

  const formatSeason = (className) => {
    switch (className) {
      case "low-season":
        return t("input.custom.hiddenCombiCalendar.titleLow");
      case "mid-season":
        return t("input.custom.hiddenCombiCalendar.titleMid");
      case "peak-season":
        return t("input.custom.hiddenCombiCalendar.titlePeak");
      default:
        return "";
    }
  };

  const MonthCalendar = ({
    disabled,
    values,
    displayError,
    ...calendarProps
  }) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const thresholdDate = new Date(currentYear, currentMonth + 3, 1);

    const isMonthDisabled = (monthValue) => {
      const monthDate = new Date(year, monthValue, 1);
      return disabled || monthDate <= thresholdDate;
    };

    const isYearDisabled = (targetYear) =>
      disabled || targetYear === currentYear;
    const isNextYearDisabled = () => disabled || year === currentYear + 2;

    const handleYearChange = (newYear) => setYear(newYear);
    const handleSelectChange = (e) => setYear(parseInt(e.target.value));

    const renderMonth = (month) => {
      const monthLabel = t(
        `input.custom.hiddenCombiCalendar.months.${month.value}`
      );
      const monthKey = `${year}.${month.value}`;

      return (
<div className="month-chip-wrapper" key = {monthLabel} title={`${monthLabel}: ${formatSeason(month.className)}`}>
  <Chip
    id={monthKey}
    {...calendarProps}
    label={monthLabel.slice(0, 3)}
    value={monthKey}
    checked={values?.includes(monthKey)}
    className={`month ${month.className || ""}`}
    disabled={isMonthDisabled(month.value)}
  />
</div>
      );
    };

    return (
      <div
        className={`container month-calendar ${disabled ? "disabled" : ""}`}
        style={{ marginBottom: "0", marginTop: "var(--space-6)" }}
      >
        <div className="row">
          <div className="col col-12">
            <div className="month-menu">
              <IconButton
                style={{ marginRight: "var(--space-2)" }}
                icon="chevron-left"
                disabled={isYearDisabled(year)}
                onClick={() => handleYearChange(year - 1)}
              />
              <Select
                options={YEAR_OPTIONS}
                value={year}
                onChange={handleSelectChange}
              />
              <IconButton
                style={{ marginLeft: "var(--space-2)" }}
                icon="chevron"
                disabled={isNextYearDisabled()}
                onClick={() => handleYearChange(year + 1)}
              />
            </div>
          </div>
        </div>

        <div className="row" style={{ marginBottom: "0" }}>
          <div
            className="col col-12"
            style={{ display: "flex", alignItems: "baseline" }}
          >
            <span
              className={`year-col ${isYearDisabled(year) ? "disabled" : ""}`}
            >
              {year - 1}
            </span>

            <div className="month-group">{MONTHS_OPTIONS.map(renderMonth)}</div>

            <span
              className={`year-col ${isNextYearDisabled() ? "disabled" : ""}`}
              onClick={() => handleYearChange(year + 1)}
            >
              {year + 1}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const DateCalendar = ({ disabled }) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const thresholdDate = new Date(currentYear, currentMonth + 3, 1);
    
    const isDateDisabled = disabled;
    const minDate = thresholdDate.toISOString().split('T')[0]; // Convert to YYYY-MM-DD format
  
    return (
      <div>
        <div
          className="row form-row date-calendar"
          style={{ display: "flex", textAlign: "center" }}
        >
          <div
            className="col col-12 col-sm-6"
            style={{
              display: "flex",
              justifyContent: "center",
              borderRight: "1px solid var(--color-achromatic-base-400)",
            }}
          >
            <div className={`date-calendar ${isDateDisabled ? "disabled" : ""}`}>
              <h3 className="text lead medium semibold">
                {t("input.custom.hiddenCombiCalendar.dateCalendar.start")}
              </h3>
              <Calendar
                name={`${name}.earliestOutboundDate`}
                {...outboundBind}
                disabled={isDateDisabled}
                min={minDate}
                displayError={outboundBind.displayError && !isDateDisabled}
              />
            </div>
          </div>
  
          <div className="col col-12 display-mobile-only">
            <Divider className="thin passive" />
          </div>
  
          <div
            className="col col-12 col-sm-6"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <div className={`date-calendar ${isDateDisabled ? "disabled" : ""}`}>
              <h3 className="text lead medium semibold">
                {t("input.custom.hiddenCombiCalendar.dateCalendar.end")}
              </h3>
              <Calendar
                name={`${name}.latestHomeboundDate`}
                min={outboundBind.value || minDate}
                {...homeboundBind}
                disabled={isDateDisabled}
                displayError={homeboundBind.displayError && !isDateDisabled}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SeasonDescriptions = () => {
    const items = [
      {
        level: "low",
        title: t("input.custom.hiddenCombiCalendar.titleLow"),
        description: t("input.custom.hiddenCombiCalendar.descriptionLow"),
      },
      {
        level: "mid",
        title: t("input.custom.hiddenCombiCalendar.titleMid"),
        description: t("input.custom.hiddenCombiCalendar.descriptionMid"),
      },
      {
        level: "peak",
        title: t("input.custom.hiddenCombiCalendar.titlePeak"),
        description: t("input.custom.hiddenCombiCalendar.descriptionPeak"),
      },
    ];
  
    return (
      <div
      className="form-row"
      style={{ display: "flex", justifyContent: "space-around" }}
    >
      {items.map(({ level, title, description }) => (
        <div
          key={level}
          className="description"
          style={{ display: "flex", alignItems: "center" }}
        >
          <div
            className={`legend-box ${level}`}
            role="img"
            aria-label={title}
            title={title}
            style={{ marginRight: "8px" }}
          ></div>
          <div>
            <div
              className="text lead small"
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <div
              className="text base small"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        </div>
      ))}
    </div>
    
    );
  };
  
  
  

  return (
    <InputHidden
      error={error}
      displayError={displayError}
      label={translatedLabel}
      placeholder={translatedPlaceholder}
      {...props}
      value={textValue}
      trailingIcon="calendar"
    >
      <div className="container form-hidden-combi-calendar">
        <div className="row">
          <div className="col col-12">
            <div
              className="text base"
              dangerouslySetInnerHTML={{
                __html: t("input.custom.hiddenCombiCalendar.description"),
              }}
            />
          </div>
        </div>

        <div
          className="row"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ flex: 1 }}>
            <Divider className="thin primary" />
          </div>

          <div style={{ margin: "0 var(--space-2)" }}>
            <Toggle
              name="flexible"
              value={type}
              onChange={(e) => setType(e.target.value)}
              options={[
                {
                  label: t("input.custom.hiddenCombiCalendar.byMonth"),
                  value: "month",
                },
                {
                  label: t("input.custom.hiddenCombiCalendar.byDate"),
                  value: "date",
                },
              ]}
            />
          </div>

          <div style={{ flex: 1 }}>
            <Divider className="thin primary" />
          </div>
        </div>

        <div className="row">
          <div className="col col-12">
            <MonthCalendar
              name={`${name}.departure`}
              market={market}
              {...monthBind}
              disabled={type !== "month"}
              displayError={monthBind.displayError && type === "month"}
            />
          </div>
        </div>

        <div>
          <DateCalendar disabled={type !== "date"} />
          {type === "month" && <SeasonDescriptions />}
        </div>
      </div>
    </InputHidden>
  );
};

export default FormHiddenCombiCalendar;
